import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ArrowUpRight, Sun, Moon } from "lucide-react";
import { PILLARS } from "../data/services";

const NAV = [
    { to: "/", label: "Home", testId: "nav-home-link" },
    { to: "/about", label: "About", testId: "nav-about-link" },
    { to: "/industries", label: "Industries", testId: "nav-industries-link" },
    { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export const Header = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [servicesOpen, setServicesOpen] = useState(false);
    const [theme, setTheme] = useState(() => localStorage.getItem("ig-theme") || "dark");
    const location = useLocation();
    const dropdownRef = useRef(null);

    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        localStorage.setItem("ig-theme", theme);
    }, [theme]);

    useEffect(() => {
        setMobileOpen(false);
        setServicesOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") {
                setServicesOpen(false);
                setMobileOpen(false);
            }
        };
        const onPointerDown = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setServicesOpen(false);
            }
        };
        document.addEventListener("keydown", onKey);
        document.addEventListener("pointerdown", onPointerDown);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.removeEventListener("pointerdown", onPointerDown);
        };
    }, []);

    return (
        <header
            className="sticky top-0 z-50 border-b border-white/10 backdrop-blur-xl"
            style={{ background: "rgba(10, 15, 30, 0.85)" }}
            data-testid="site-header"
        >
            <div className="container-x flex h-[72px] items-center justify-between gap-4">
                <Link to="/" className="flex items-center gap-3" aria-label="Innovated Global IT Solutions — home" data-testid="nav-logo-link">
                    <span
                        className="relative inline-block"
                        style={{ height: "40px", aspectRatio: "1370 / 919" }}
                        role="img"
                        aria-label="Innovated Global IT Solutions logo"
                    >
                        <span aria-hidden="true" className="absolute top-0" style={{ left: "25.11%", width: "49.78%" }}>
                            <img src="/assets/flower-mark.png" alt="" className="header-flower-spin w-full h-auto" />
                        </span>
                        <img src="/assets/logo-name.png" alt="" aria-hidden="true" className="logo-ondark absolute left-0 w-full" style={{ top: "69%" }} />
                        <img src="/assets/logo-name-dark.png" alt="" aria-hidden="true" className="logo-onlight absolute left-0 w-full" style={{ top: "69%" }} />
                        <img src="/assets/logo-tagline.png" alt="" aria-hidden="true" className="logo-ondark absolute left-0 w-full" style={{ top: "94.3%" }} />
                        <img src="/assets/logo-tagline-dark.png" alt="" aria-hidden="true" className="logo-onlight absolute left-0 w-full" style={{ top: "94.3%" }} />
                    </span>
                </Link>

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
                    <NavLink
                        to="/"
                        data-testid="nav-home-link"
                        className={({ isActive }) =>
                            `nav-item rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                                isActive ? "nav-active text-white" : "text-slate-400 hover:text-white"
                            }`
                        }
                    >
                        Home
                    </NavLink>

                    <div
                        className="relative"
                        ref={dropdownRef}
                        onMouseEnter={() => setServicesOpen(true)}
                        onMouseLeave={() => setServicesOpen(false)}
                    >
                        <button
                            type="button"
                            data-testid="nav-services-toggle"
                            aria-expanded={servicesOpen}
                            aria-haspopup="true"
                            aria-controls="services-menu"
                            onClick={() => setServicesOpen((v) => !v)}
                            className={`nav-item flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                                location.pathname.startsWith("/services") ? "nav-active text-white" : "text-slate-400 hover:text-white"
                            }`}
                        >
                            Services
                            <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? "rotate-180" : ""}`} />
                        </button>
                        {servicesOpen && (
                            <div
                                id="services-menu"
                                className="menu-glass absolute left-1/2 top-full w-72 -translate-x-1/2 rounded-2xl p-2 pt-3"
                                data-testid="services-dropdown"
                            >
                                {PILLARS.map((p) => (
                                    <Link
                                        key={p.slug}
                                        to={`/services/${p.slug}`}
                                        data-testid={`nav-service-${p.slug}`}
                                        className="group flex items-center justify-between rounded-xl px-4 py-3 transition-colors duration-200 hover:bg-white/5"
                                    >
                                        <span>
                                            <span className="block text-sm font-semibold text-white">{p.name}</span>
                                            <span className="block text-xs text-slate-400">{p.tagline}</span>
                                        </span>
                                        <span
                                            className="h-2 w-2 rounded-full transition-transform duration-200 group-hover:scale-150"
                                            style={{ background: p.accent }}
                                        />
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>

                    {NAV.slice(1).map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            data-testid={item.testId}
                            className={({ isActive }) =>
                                `nav-item rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                                    isActive ? "nav-active text-white" : "text-slate-400 hover:text-white"
                                }`
                            }
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
                        aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
                        data-testid="theme-toggle"
                        className="theme-toggle-btn flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-colors duration-200 hover:text-white"
                    >
                        {theme === "dark" ? <Sun size={17} aria-hidden="true" /> : <Moon size={17} aria-hidden="true" />}
                    </button>
                    <Link to="/contact" className="btn-primary hidden sm:inline-flex !px-6 !py-2.5" data-testid="nav-lets-talk-button">
                        Let's Talk
                        <ArrowUpRight size={16} />
                    </Link>
                    <button
                        type="button"
                        className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
                        aria-expanded={mobileOpen}
                        aria-controls="mobile-menu"
                        aria-label={mobileOpen ? "Close navigation menu" : "Open navigation menu"}
                        onClick={() => setMobileOpen((v) => !v)}
                        data-testid="mobile-menu-button"
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {mobileOpen && (
                <nav id="mobile-menu" className="border-t border-white/10 lg:hidden" aria-label="Mobile" data-testid="mobile-menu">
                    <div className="container-x flex flex-col gap-1 py-4">
                        <NavLink to="/" data-testid="mobile-nav-home" className="rounded-xl px-4 py-3 text-sm font-medium text-slate-200 hover:bg-white/5">
                            Home
                        </NavLink>
                        <p className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Services</p>
                        {PILLARS.map((p) => (
                            <NavLink
                                key={p.slug}
                                to={`/services/${p.slug}`}
                                data-testid={`mobile-nav-service-${p.slug}`}
                                className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-200 hover:bg-white/5"
                            >
                                <span className="h-2 w-2 rounded-full" style={{ background: p.accent }} />
                                {p.name}
                            </NavLink>
                        ))}
                        <NavLink to="/about" data-testid="mobile-nav-about" className="rounded-xl px-4 py-3 text-sm font-medium text-slate-200 hover:bg-white/5">
                            About
                        </NavLink>
                        <NavLink to="/industries" data-testid="mobile-nav-industries" className="rounded-xl px-4 py-3 text-sm font-medium text-slate-200 hover:bg-white/5">
                            Industries
                        </NavLink>
                        <NavLink to="/contact" data-testid="mobile-nav-contact" className="rounded-xl px-4 py-3 text-sm font-medium text-slate-200 hover:bg-white/5">
                            Contact
                        </NavLink>
                        <Link to="/contact" className="btn-primary mt-3" data-testid="mobile-lets-talk-button">
                            Let's Talk
                            <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </nav>
            )}
        </header>
    );
};
